import React from "react";
import { Helmet } from "react-helmet-async";

export default function AirShieldTransitZone() {
  return (
    <section className="w-full">
      {/* ============================
          SEO
      ============================ */}
      <Helmet>
        <title>AirShield™ Transit Zone | Clean Air Infrastructure by Aerzen Air™</title>
        <meta
          name="description"
          content="AirShield™ Transit Zone by Aerzen Air™ is a pilot-ready clean air infrastructure concept for public transit environments, reducing pollution exposure during waiting time."
        />
        <link
          rel="canonical"
          href="https://www.aerzenair.com/initiatives/airshield-transit-zone"
        />
      </Helmet>

      {/* ======================================================
          HERO (INSTITUTIONAL / WHITE)
      ====================================================== */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-28">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            AERZEN AIR™ AirShield™ Transit Zone
          </h1>

          <p className="text-lg md:text-xl mb-4 text-gray-800">
            Clean Air Infrastructure for Public Transit Environments
          </p>

          <p className="max-w-3xl text-sm md:text-base text-gray-600">
            A pilot-ready urban initiative focused on reducing pollution exposure
            during public waiting time.
          </p>
        </div>
      </div>

      {/* ======================================================
          WHY THIS MATTERS
      ====================================================== */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-semibold mb-6">
          The Urban Waiting-Time Problem
        </h2>

        <p className="text-gray-700 max-w-4xl mb-4">
          Urban commuters are exposed to high levels of air pollution while waiting
          at bus stops and transit points. This exposure is often unavoidable and
          disproportionately affects daily commuters, senior citizens, and children.
        </p>

        <p className="text-gray-700 max-w-4xl">
          Conventional transit infrastructure offers little to no protection against
          airborne pollutants during these waiting periods.
        </p>
      </div>

      {/* ======================================================
          AERZEN AIR™ VISION
      ====================================================== */}
      <div className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl font-semibold mb-6">
            Aerzen Air™ Vision for Healthier Transit Spaces
          </h2>

          <p className="text-gray-700 max-w-4xl mb-4">
            <strong>AERZEN AIR™</strong> envisions transforming conventional bus stops into
            Clean Air Waiting Zones — enclosed, purified micro-environments
            designed to reduce pollutant exposure during waiting time.
          </p>

          <p className="text-gray-700 max-w-4xl">
            The AirShield™ Transit Zone is designed to be modular, scalable,
            transparent, and aligned with public-health and smart-city objectives.
          </p>
        </div>
      </div>

      {/* ======================================================
          CONCEPT OVERVIEW – TYPE A / B / C
      ====================================================== */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-semibold mb-12">
          AERZEN AIR™ AirShield™ Transit Zone – Concept Overview
        </h2>

        {/* TYPE A */}
        <div className="mb-20">
          <img
            src="/images/initiatives/airshield-type-a.png"
            alt="Aerzen Air AirShield Transit Zone Type A high footfall configuration"
            className="w-full rounded-xl border mb-6"
          />
          <h3 className="text-xl font-semibold mb-2">
            Type A — High Footfall
          </h3>
          <p className="text-gray-700 max-w-4xl">
            Multi-chamber enclosed clean air zone designed for terminals and
            high-density transit locations.
          </p>
        </div>

        {/* TYPE B */}
        <div className="mb-20">
          <img
            src="/images/initiatives/airshield-type-b.png"
            alt="Aerzen Air AirShield Transit Zone Type B medium footfall configuration"
            className="w-full rounded-xl border mb-6"
          />
          <h3 className="text-xl font-semibold mb-2">
            Type B — Medium Footfall
          </h3>
          <p className="text-gray-700 max-w-4xl">
            Two-chamber configuration suitable for standard urban bus stops
            with moderate commuter volume.
          </p>
        </div>

        {/* TYPE C */}
        <div>
          <img
            src="/images/initiatives/airshield-type-c.png"
            alt="Aerzen Air AirShield Transit Zone Type C low footfall configuration"
            className="w-full rounded-xl border mb-6"
          />
          <h3 className="text-xl font-semibold mb-2">
            Type C — Low Footfall
          </h3>
          <p className="text-gray-700 max-w-4xl">
            Single-chamber compact clean air shelter intended for low-traffic
            transit points.
          </p>
        </div>
      </div>

      {/* ======================================================
          ENVIRONMENTAL & SOCIAL IMPACT
      ====================================================== */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-semibold mb-6">
          Environmental & Social Impact
        </h2>

        <ul className="text-gray-700 space-y-3 max-w-4xl">
          <li>• Reduces pollution exposure during public waiting time</li>
          <li>• Supports healthier public transport usage</li>
          <li>• Enhances public awareness of air quality</li>
          <li>• Aligns with CSR, ESG, and smart-city objectives</li>
        </ul>
      </div>

      {/* ======================================================
          PROJECT STATUS
      ====================================================== */}
      <div className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl font-semibold mb-6">
            Project Status
          </h2>

          <p className="text-gray-700 max-w-4xl">
            Concept & pilot-ready initiative.  
            The AERZEN AIR™ AirShield™ Transit Zone is currently proposed for pilot
            implementation in collaboration with government bodies and
            institutional partners.
          </p>
        </div>
      </div>

      {/* ======================================================
          COLLABORATION CTA
      ====================================================== */}
      <div className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl font-semibold mb-6">
          Collaborate for Cleaner Transit Spaces
        </h2>

        <p className="text-gray-700 max-w-3xl mx-auto mb-8">
          Aerzen Air™ welcomes collaboration with government bodies, urban authorities,
          and institutional partners interested in pilot initiatives and
          environmental programs.
        </p>

        <a
          href="/contact"
          className="inline-block px-8 py-4 rounded-lg bg-gray-900 text-white hover:bg-gray-800 transition"
        >
          Contact Us
        </a>
      </div>
    </section>
  );
}
